import {Toast} from 'antd-mobile';
import {PickUpBase64ToFile, PICKED_UP_FAILED_KEY} from "../../../services/PickedUpDataService";
import {
  PickUpImageCompressionOptions,
  isPickUpNativeBridgeAvailable,
  PickUpNativePermissions, PickUpNativeCamera
} from "../../pickup/components/PickUpPhotoNativeBridge";
import geolocationService from "../../pickup/services/PickUpGeolocationService";
import { useLocationStore } from "../../../store/locationStore";
import i18n from 'i18n';
import { fileUpload } from '../../../utils/storageUtils';

export type TransportPositionType = 'signFor' | 'packageImg' | 'doorNumber' | 'warehousePhoto' | 'sealPhoto1' | 'sealPhoto2' | 'sealPhoto3' | 'departureSealPhoto1' | 'departureSealPhoto2' | 'departureSealPhoto3' | 'departureBolPhoto1' | 'departureBolPhoto2' | 'departureBolPhoto3';
export type TransportPhotoType = {url:string,objectKey:string};
export type TransportPhotoUpdateCallback = (updateFn: (prev: Record<TransportPositionType, TransportPhotoType | undefined>) => Record<TransportPositionType, TransportPhotoType | undefined>) => void;

class TransportPhotoService {
  async fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  async handlePhotoNotUpload(
      type: TransportPositionType,
      file: File,
      onPhotosUpdate: TransportPhotoUpdateCallback,
  ): Promise<void> {
    try {
      const base64Data = await this.fileToBase64(file);
      onPhotosUpdate((prev) => ({
        ...prev,
        [type]: { url: base64Data, objectKey: PICKED_UP_FAILED_KEY}
      }));
    } catch (error) {
      console.error('Failed handle photo not upload:', error);
      Toast.show({ content: i18n.t('pages.pickUp.photo.uploadFailed'), position: 'bottom' });
      throw error;
    }
  }

  /**
   * 处理删除照片
   * @param type 照片类型
   * @param onPhotosUpdate 照片更新回调
   * @returns Promise<void>
   */
  async handleDeletePhoto(
    type: TransportPositionType,
    onPhotosUpdate: TransportPhotoUpdateCallback
  ): Promise<void> {
    onPhotosUpdate((prev) => {
      const newState = { ...prev };
      delete newState[type];
      return newState;
    });
    Toast.show({ content: i18n.t('pages.tms.transporting.arriving.photoDeleted'), position: 'bottom' });
  }
}

/**
 * 上传失败的运输照片
 * @param photos 需要上传的照片数组
 * @returns Promise<string[]> 上传成功后的照片key数组
 */
export const uploadFailedTransportPhotos = async (photos: TransportPhotoType[]): Promise<string[]> => {
  console.log('uploadFailedTransportPhotos:', photos.map(item => item.objectKey));

  const uploadOnePhoto = async (objectUrl: string): Promise<string> => {
    return (await fileUpload(await PickUpBase64ToFile(objectUrl, Date.now() + '.webp'), 'transport')).objectKey;
  };

  return await Promise.all(photos.map(async (photo) => {
    if (photo.objectKey !== PICKED_UP_FAILED_KEY) {
      return photo.objectKey;
    }

    return await uploadOnePhoto(photo.url);
  }));
};

/**
 * 带超时控制的图片上传方法
 * @param photos 需要上传的图片数组
 * @param timeoutMs 超时时间（毫秒），默认5秒
 * @param showToast 是否显示上传进度提示，默认true
 * @returns Promise<string[]> 上传成功后的图片key数组
 */
export const uploadTransportPhotosWithTimeout = async (
    photos: TransportPhotoType[],
    timeoutMs: number = 5000,
    showToast: boolean = true
): Promise<string[]> => {
  let uploadingToast: any = null;

  if (showToast) {
    uploadingToast = Toast.show({
      content: i18n.t('pages.pickUp.uploadPhotoText'),
      position: 'center',
      duration: 0,
    });
  }

  try {
    // 创建超时 Promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        const error = new Error('网络异常：图片上传超时');
        // 添加 networkError 属性，使其被识别为网络异常
        (error as any).networkError = true;
        reject(error);
      }, timeoutMs);
    });

    // 创建上传 Promise
    const uploadPromise = uploadFailedTransportPhotos(photos);

    // 使用 Promise.race 来实现超时控制
    const photoKeys = await Promise.race([uploadPromise, timeoutPromise]);

    // 上传成功，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }

    return photoKeys;
  } catch (error) {
    // 上传失败或超时，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }
    throw error;
  }
};

const transportPhotoService = new TransportPhotoService();
export default transportPhotoService;


